import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { t } from '@web-solutions/module-localization';

import classes from './style.module.scss';

interface BillingTermsLegalProps {
  onTermsClick: () => void;
  onBillingTermsClick: () => void;
  className?: string;
}

/**
 * "I confirm that I have read and agree to the Terms of Use and Billing Terms."
 * Used on its own when the consent checkbox is disabled — then this line is the
 * whole disclosure in the payment modal.
 */
export const BillingTermsLegal: React.FC<BillingTermsLegalProps> = ({
  onTermsClick,
  onBillingTermsClick,
  className,
}) => (
  <span className={classNames(classes.legal, className)}>
    {t('onboarding.personalPlan.plan.confirm')}{' '}
    <a onClick={onTermsClick}>{t('onboarding.personalPlan.plan.termsOfUse')}</a>{' '}
    {t('onboarding.personalPlan.plan.and')}{' '}
    <a onClick={onBillingTermsClick}>{t('onboarding.personalPlan.plan.billingTerms')}</a>.
  </span>
);

interface BillingConsentProps extends BillingTermsLegalProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Highlights the checkbox after a payment attempt without consent. */
  error?: boolean;
  /**
   * The legal line under the checkbox. It repeats what the checkbox label
   * already states, so the payment modal shows the checkbox alone and keeps the
   * line for the variant without the checkbox.
   */
  showLegal?: boolean;
}

/**
 * Required billing-terms consent shown in the payment modal instead of the
 * plain legal line when `showBillingTermsConsent` is enabled. Payment stays
 * blocked until the checkbox is ticked.
 */
export const BillingConsent = forwardRef<HTMLDivElement, BillingConsentProps>(
  (
    {
      checked,
      onChange,
      error = false,
      onTermsClick,
      onBillingTermsClick,
      className,
      showLegal = true,
    },
    ref
  ) => (
    <div className={classNames(classes.wrap, className)} ref={ref}>
      <label className={classNames(classes.consent, { [classes.error]: error })}>
        <input
          type='checkbox'
          className={classes.input}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className={classes.box} aria-hidden='true' />
        <span className={classes.consentText}>
          {t('onboarding.personalPlan.plan.billingConsent')}
        </span>
      </label>

      {showLegal && (
        <BillingTermsLegal
          onTermsClick={onTermsClick}
          onBillingTermsClick={onBillingTermsClick}
        />
      )}
    </div>
  )
);

export default BillingConsent;
